import React, {useEffect, useRef, useState} from "react";
import {Button, Checkbox, Drawer, Input, Popconfirm, Popover, Spin, Tag, Tooltip} from "antd";
import {ArrowDownOutlined, ArrowUpOutlined, CloseOutlined, EditOutlined, ExportOutlined, EyeOutlined, InboxOutlined, SettingOutlined} from "@ant-design/icons";
import {config} from "../../../../../shared/config/config";
import {mediaUrl, statusColor} from "./apartmentReferences";
import {AD_LINKS, OLD_FORM_FIELDS, OLD_LABELS} from "./oldFlatLayout";
import {FieldControl, fromForm, toForm} from "./fieldControls";

// The flats table: one flat per line, the actions on the left (the owner, 23.10: «действия всегда слева»). Any field
// this user's roles see can be a column (the owner, 27.10: «в таблицу всю вообще информацию»), chosen and ordered in
// «Колонки» and kept for each employee on the server; a double click on a cell changes its field without opening the
// flat (the owner, 27.10: «данные менять не заходя в квартиру»).

const PORTAL = config.API_HOST
const dash = <span className='cell-empty'>—</span>
const filled = (v) => v !== null && v !== undefined && v !== '' && v !== '-'
const num = (v, digits = 0) => (filled(v) && Number.isFinite(Number(v)) ? Number(v).toLocaleString('ru-RU', {maximumFractionDigits: digits}) : filled(v) ? String(v) : dash)
// «2026-10-22» as 22.10.26, without time zones
const day = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}` : null
}
const dayTime = (v) => {
  const d = v ? new Date(v) : null
  return d && !Number.isNaN(d.getTime())
    ? `${d.toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit'})} ${d.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'})}`
    : null
}
const text = (v) => (filled(v) ? v : dash)
// a long value in one line, the whole of it on hover
const clip = (v) => (filled(v) ? <Tooltip title={v} mouseEnterDelay={0.4}><span>{v}</span></Tooltip> : dash)
// an ad's address opens in a new tab, as on ECI old; what is not an address («-», «нет в рекламе») stays text. The link
// leaves room at the end of the cell for the double click that changes it
const adLink = (v) => {
  const url = String(v === null || v === undefined ? '' : v).trim()
  if (!/^https?:\/\/\S+$/i.test(url)) return filled(v) ? <Tooltip title={v} mouseEnterDelay={0.4}><span className='cell-link'>{v}</span></Tooltip> : dash
  return (
    <Tooltip title={url} mouseEnterDelay={0.4}>
      <a className='cell-link' href={url} target='_blank' rel='noopener noreferrer' onClick={(e) => e.stopPropagation()}>{url}</a>
    </Tooltip>
  )
}
// «1 ккв (Евро)» → «1к Е», «Студия» → «студия»
const rooms = (v) => {
  const m = String(v || '').match(/^(\d+)\s*ккв(.*)$/)
  if (!m) return filled(v) ? String(v).toLowerCase() : dash
  return `${m[1]}к${/евро/i.test(m[2]) ? ' Е' : ''}`
}
// «2 - квартиры с рынка» → «2», «1АЦ - обычные эксклюзивы» → «1АЦ»; the whole name on hover
const adsShort = (v) => (filled(v)
  ? <Tooltip title={v} mouseEnterDelay={0.4}><span>{String(v).split(/\s+-\s+/)[0]}</span></Tooltip>
  : dash)
const place = ({queue, build, section}) => [queue && `оч.${queue}`, build && `к.${build}`, section && section !== '-' && `с.${section}`]
  .filter(Boolean).join(' ')
const photo = (file) => (file
  ? (
    <Popover placement='right' mouseEnterDelay={0.15} content={<img className='flats-photo-large' src={mediaUrl(file)} alt=''/>}>
      <img className='flats-photo' src={mediaUrl(file)} alt='' loading='lazy'/>
    </Popover>
  )
  : <span className='flats-photo flats-photo--empty'/>)

// «Площадь» as ECI old's lists and the portal show it: приведённая, the area without balconies and loggias plus the
// summer rooms with their coefficients (reducedArea, worked out by the database at every save); the sum on hover
const SUMMER = [['loggiaArea', 'лоджия', 0.5], ['balconyArea', 'балкон', 0.3]]
const summerOf = (row) => SUMMER.filter(([key]) => Number(row[key]) > 0)
const reducedHint = (row) => (summerOf(row).length
  ? [`${num(row.totalSquare, 2)} без балконов/лоджий`, ...summerOf(row)
    .map(([key, name, k]) => `${name} ${num(row[key], 2)} × ${String(k).replace('.', ',')}`)].join(' + ')
  : 'Лоджия и балкон не указаны: площадь без балконов/лоджий. Если они есть, заполните «Лоджия» или «Балкон» в карточке.')

// what the list always sends
const ALWAYS = ['id', 'residentialComplex', 'queue', 'build', 'section', 'updatedAt']

// The table's own columns: key — the column (and the field when it shows one field), fields — what the list must send
// for it, edit — the field a double click changes, sort — the list's sort key, standard — shown until the employee
// chooses, covers — fields this column already shows (no second column for them), also — fields it shows when the user
// sees them (asked for, but the column stays without them)
const CUSTOM = [
  {key: 'photo', title: '', label: 'Фото (планировка)', width: 40, fields: ['layoutPhoto'], covers: ['layoutPhoto'], standard: true,
    render: ({layoutPhoto}) => photo(layoutPhoto)},
  {key: 'id', title: 'ID', label: 'ID и № старого сайта', width: 132, fields: [], standard: true,
    render: ({id, eciOldId}) => (
      <>
        <a href={`${PORTAL}/apartments/${id}`} target='_blank' rel='noopener noreferrer'>{id}</a>
        <span className='cell-hint'>{eciOldId ? ` · №${eciOldId}` : ' · новая'}</span>
      </>
    )},
  // the developer on hover, as ECI old shows it over a ЖК (staff check it there, 28.10); a Запас flat is marked before
  // its ЖК (the owner, 02.11: told apart at a glance)
  {key: 'complex', title: 'ЖК', label: 'ЖК, очередь, корпус, секция', width: 210, fields: [], also: ['builder'], standard: true, ellipsis: true,
    render: (row) => (
      <Tooltip
        title={(
          <>
            <div>{[row.residentialComplex, place(row)].filter(Boolean).join(', ')}</div>
            {row.builder !== undefined && <div>Застройщик: {filled(row.builder) ? row.builder : 'не указан'}</div>}
          </>
        )}
        mouseEnterDelay={0.4}
      >
        <span>
          {row.stock && <Tag color='orange' className='flat-stock-tag'>Запас</Tag>}
          {row.archived && <Tag className='flat-stock-tag'>Архив</Tag>}
          {row.residentialComplex || '—'}
        </span> <span className='cell-hint'>{place(row)}</span>
      </Tooltip>
    )},
  {key: 'queue', title: 'Очередь', label: 'Очередь', width: 110, fields: [], ellipsis: true, render: ({queue}) => clip(queue)},
  {key: 'build', title: 'Корпус', label: 'Корпус', width: 72, fields: [], render: ({build}) => text(build)},
  {key: 'section', title: 'Секция', label: 'Секция', width: 72, fields: [], render: ({section}) => text(section)},
  {key: 'floor', title: 'Эт.', label: 'Этаж / этажность', width: 60, fields: ['floor', 'totalFloors'], edit: 'floor', sort: 'floor', standard: true,
    render: ({floor, totalFloors}) => (filled(floor) ? `${floor}${filled(totalFloors) ? `/${totalFloors}` : ''}` : dash)},
  {key: 'numberOfRooms', title: 'Комн.', width: 64, fields: ['numberOfRooms'], edit: 'numberOfRooms', standard: true,
    render: ({numberOfRooms}) => <Tooltip title={numberOfRooms} mouseEnterDelay={0.4}><span>{rooms(numberOfRooms)}</span></Tooltip>},
  // the key stays «totalSquare», which the saved column sets have (owner 03.11.2026: «мы должны видеть тут 27.01»)
  {key: 'totalSquare', title: 'Пл., м²', label: 'Площадь (приведённая: лоджия × 0,5, балкон × 0,3)', width: 76, align: 'right',
    fields: ['reducedArea'], needs: 'totalSquare', also: ['totalSquare', 'loggiaArea', 'balconyArea'], covers: ['reducedArea'], sort: 'reducedArea',
    standard: true,
    render: (row) => (filled(row.reducedArea)
      ? <Tooltip title={reducedHint(row)} mouseEnterDelay={0.4}><span>{num(row.reducedArea, 2)}</span></Tooltip>
      : dash)},
  {key: 'areaWithoutSummer', title: 'Пл. без балк., м²', label: 'Площадь без балконов/лоджий', width: 96, align: 'right',
    fields: ['totalSquare'], edit: 'totalSquare', sort: 'totalSquare', render: ({totalSquare}) => num(totalSquare, 2)},
  // «Цена» as the portal and ECI old's lists show it (fullPrice, worked out by the database: на руки, залог, налог с
  // разницы с договором; GF Lermontovsky and the developers' flats by their own rules); the key stays «price», which the
  // saved column sets have (owner 03.11.2026: «на портале всё ок, а в админке цена неправильная»)
  {key: 'price', title: 'Цена', label: 'Цена (как на портале и в списках старого сайта)', width: 108, align: 'right',
    fields: ['fullPrice'], needs: 'price', also: ['price'], covers: ['fullPrice'], sort: 'price', standard: true,
    render: ({fullPrice, price}) => (filled(fullPrice) && filled(price) && Number(fullPrice) !== Number(price)
      ? <Tooltip title={`При 100% оплате (на руки + залог): ${num(price)}`} mouseEnterDelay={0.4}><span>{num(fullPrice)}</span></Tooltip>
      : num(filled(fullPrice) ? fullPrice : price))},
  // ECI old's price at 100% payment, на руки + залог: worked out at every save as ECI old does it (backend
  // service/oldPrices.js), not typed in
  {key: 'price100', title: 'Цена 100%', label: 'Цена при 100% оплате (на руки + залог)', width: 108, align: 'right', fields: ['price'],
    sort: 'price100', render: ({price}) => num(price)},
  {key: 'actualPrice', title: 'На руки', width: 108, align: 'right', fields: ['actualPrice'], edit: 'actualPrice', sort: 'actualPrice', standard: true,
    render: ({actualPrice}) => num(actualPrice)},
  {key: 'statuses', title: 'Статус', width: 116, fields: ['statuses'], edit: 'statuses', sort: 'statuses', standard: true,
    render: ({statuses, archived}) => {
      const tag = statuses ? <Tag color={statusColor(statuses)}>{statuses}</Tag> : dash
      // in ECI old's archive: shown only with «Архив» chosen
      return archived ? (
        <Tooltip title={`В архиве: старый сайт убрал квартиру из своей таблицы.${statuses ? ` Статус: ${statuses}.` : ''}`} mouseEnterDelay={0.4}>
          <Tag>Архив</Tag>
        </Tooltip>
      ) : tag
    }},
  {key: 'gradeName', title: 'Оц.', width: 56, fields: ['gradeName'], edit: 'gradeName', sort: 'gradeName', standard: true,
    render: ({gradeName}) => (gradeName === 'Инвестиционная' ? <Tooltip title={gradeName}><span>Инв</span></Tooltip> : text(gradeName))},
  {key: 'ads', title: 'Рекл.', width: 64, fields: ['ads'], edit: 'ads', sort: 'ads', standard: true, render: ({ads}) => adsShort(ads)},
  {key: 'salesman', title: 'Продавец', width: 92, fields: ['salesman'], edit: 'salesman', sort: 'salesman', standard: true, ellipsis: true,
    render: ({salesman}) => clip(salesman)},
  {key: 'deadline', title: 'Срок', width: 104, fields: ['deadline'], edit: 'deadline', sort: 'deadline', standard: true, ellipsis: true,
    render: ({deadline}) => clip(deadline)},
  {key: 'actualizedAt', title: 'Актуализация', label: 'Актуализация (статус и дата)', width: 156, fields: ['actualizationStatus', 'actualizedAt'],
    sort: 'actualizedAt', standard: true, ellipsis: true,
    render: ({actualizationStatus, actualizedAt}) => (filled(actualizationStatus) || actualizedAt
      ? (
        <Tooltip title={[actualizationStatus, day(actualizedAt)].filter(Boolean).join(' · ')} mouseEnterDelay={0.4}>
          <span>{filled(actualizationStatus) ? actualizationStatus : '—'}</span>
          {actualizedAt && <span className='cell-hint'> · {day(actualizedAt)}</span>}
        </Tooltip>
      )
      : dash)},
  {key: 'updatedAt', title: 'Изменено', label: 'Когда изменена', width: 104, fields: [], sort: 'updatedAt', standard: true,
    render: ({updatedAt}) => dayTime(updatedAt) || dash},
  {key: 'district', title: 'Район', label: 'Район (на портале)', width: 120, fields: ['districtName', 'district'], ellipsis: true,
    render: ({districtName, district}) => clip(districtName || district)},
  // ECI old's «Первый взнос в рассрочку», as the card works it out
  {key: 'firstPayment', title: 'Первый взнос', label: 'Первый взнос в рассрочку', width: 112, align: 'right', fields: ['firstPayment'],
    needs: 'installmentSchedule',
    render: ({firstPayment}) => (firstPayment ? (firstPayment.amount ? num(firstPayment.amount) : <span className='cell-hint'>по графику</span>) : dash)},
]
const CUSTOM_KEYS = new Set(CUSTOM.map((c) => c.key))
const COVERED = new Set(CUSTOM.flatMap((c) => c.covers || []))
export const STANDARD_COLUMNS = CUSTOM.filter((c) => c.standard).map((c) => c.key)

// the keys of the picker before 27.10 (kept in the browser), as the columns are named now
const OLD_KEYS = {rooms: 'numberOfRooms', status: 'statuses', grade: 'gradeName', kitchen: 'kitchenSquare', living: 'livingRoomSquare',
  decor: 'decorName', encumbrance: 'paymentLeftText', bank: 'encumbranceBank', reducedSquare: 'totalSquare'}
export const renameOldKeys = (keys) => [...new Set(keys.map((key) => OLD_KEYS[key] || key))]

// any other field as a column, drawn by its kind
const SORT_KINDS = ['text', 'long', 'int', 'number', 'bool', 'date', 'curator']
const WIDTHS = {int: 96, number: 96, bool: 64, date: 92, photo: 48, files: 72, arrows: 80, features: 200, ids: 80, long: 180}
const renderOf = ({key, kind}, references) => (row) => {
  const value = row[key]
  if (AD_LINKS[key]) return adLink(value)
  switch (kind) {
    case 'int':
      return num(value)
    case 'number':
      return num(value, 2)
    case 'bool':
      return value === true ? 'Да' : value === false ? 'Нет' : dash
    case 'date':
      return day(value) || dash
    case 'list':
      return Array.isArray(value) && value.length ? clip(value.join(', ')) : dash
    case 'photo':
      return photo(value)
    case 'files':
      return Array.isArray(value) && value.length ? `${value.length} фото` : dash
    case 'arrows':
      return value && typeof value === 'object' && Object.keys(value).length ? `${Object.keys(value).length} стр.` : dash
    // the features' names, as the portal shows them
    case 'features':
      return Array.isArray(row.filters) && row.filters.length ? clip(row.filters.join(', ')) : dash
    case 'ids':
      return Array.isArray(value) && value.length ? String(value.length) : dash
    case 'curator': {
      const curator = references && references.curators.find((c) => c.value === String(value))
      return clip(curator ? curator.label : value)
    }
    default:
      return clip(value === null || value === undefined ? value : String(value))
  }
}
const generic = (field, references) => ({
  key: field.key,
  title: AD_LINKS[field.key] || OLD_LABELS[field.key] || field.title,
  label: AD_LINKS[field.key] || OLD_LABELS[field.key] || field.title,
  fields: [field.key],
  edit: field.key,
  sort: SORT_KINDS.includes(field.kind) ? field.key : null,
  width: AD_LINKS[field.key] ? 200 : WIDTHS[field.kind] || 140,
  align: ['int', 'number'].includes(field.kind) ? 'right' : undefined,
  ellipsis: true,
  render: renderOf(field, references),
})

// every column this user may show, by group: the table's own, then the card's groups (meta.groups), e.g.
// [{ title, columns: [{ key, title, label, fields, edit, sort, render, … }] }]
export const catalogueOf = (meta, references) => {
  const visible = new Set([...ALWAYS, ...(meta ? meta.groups.flatMap((g) => g.fields.map((f) => f.key)) : [])])
  const own = CUSTOM
    // a column worked out from another field (needs) is seen by whoever sees that field
    .filter((c) => !meta || (c.needs ? visible.has(c.needs) : c.fields.every((field) => visible.has(field))))
    .map((c) => ({...c, label: c.label || OLD_LABELS[c.key] || c.title}))
  const groups = (meta ? meta.groups : []).map((group) => ({
    title: group.title,
    columns: group.fields.filter((f) => !CUSTOM_KEYS.has(f.key) && !COVERED.has(f.key)).map((f) => generic(f, references)),
  })).filter((group) => group.columns.length)
  return [{title: 'Таблица', columns: own}, ...groups]
}

// the fields the list has to send for these columns (?fields=)
export const fieldsOf = (keys, catalogue) => {
  const byKey = Object.fromEntries(catalogue.flatMap((g) => g.columns).map((c) => [c.key, c]))
  return [...new Set(keys.flatMap((key) => (byKey[key] ? [...byKey[key].fields, ...(byKey[key].also || [])] : [])))]
}

// the column of a header click → the list's sort key
export const sortKeyOf = (key, catalogue) => {
  const column = catalogue.flatMap((g) => g.columns).find((c) => c.key === key)
  return column ? column.sort : null
}

// sort: the list's «sort» parameter («price», «-price»); a column's own order follows it
const orderOf = (sort, key) => (sort === key ? 'ascend' : sort === `-${key}` ? 'descend' : null)

// a cell being changed: its field's control, saved on Enter, on leaving it, or on a choice; Esc gives up
const CellEditor = ({target, row, references, saving, onCommit, onCancel}) => {
  const {field, list} = target
  const initial = toForm(field.kind, row[field.key])
  const [value, setValue] = useState(initial)
  const done = useRef(false)
  // a failed save leaves the cell open for another try
  useEffect(() => {
    if (!saving) done.current = false
  }, [saving])
  const finish = (next) => {
    if (done.current) return
    done.current = true
    const out = fromForm(field.kind, next)
    if (JSON.stringify(out) === JSON.stringify(fromForm(field.kind, initial))) onCancel()
    else onCommit(out)
  }
  const cancel = () => {
    if (done.current) return
    done.current = true
    onCancel()
  }
  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      cancel()
    }
  }
  const multiple = field.kind === 'list'
  const choice = multiple || Boolean(list) || ['bool', 'curator'].includes(field.kind)
  const props = choice
    ? {
      defaultOpen: true,
      onChange: (v) => {
        setValue(v)
        if (!multiple) finish(v)
      },
      onBlur: () => (multiple ? finish(value) : cancel()),
    }
    : {
      onChange: (e) => setValue(e && e.target ? e.target.value : e),
      onPressEnter: () => finish(value),
      onBlur: () => finish(value),
    }
  return (
    <div className='flat-cell-editor' onClick={(e) => e.stopPropagation()}>
      <Spin spinning={saving} size='small'>
        <FieldControl field={field} list={list} references={references} plain={OLD_FORM_FIELDS.has(field.key)} size='small' autoFocus value={value} onKeyDown={onKeyDown}
          style={{width: '100%'}} {...props}/>
      </Spin>
    </div>
  )
}

// cell: { editing: { id, field } | null, saving, editableOf(field) → { field, list } | null, start(id, field), commit(id, field, value),
// cancel(), references }
// onStock(row): «В Запас» / «Из Запаса» of one flat, for the roles with «Запас» (owner 03.11: «прямо из таблицы»)
export const tableColumns = ({shown, catalogue, sort, canEdit, onEdit, cell, onStock}) => {
  const byKey = Object.fromEntries(catalogue.flatMap((g) => g.columns).map((c) => [c.key, c]))
  const targetOf = (column) => (canEdit && column.edit ? cell.editableOf(column.edit) : null)
  // the cell being changed: its field's control over it, so a narrow column keeps its width
  const renderCell = (column) => (row) => {
    const content = column.render(row)
    const target = targetOf(column)
    if (!target || !cell.editing || cell.editing.id !== row.id || cell.editing.field !== column.edit) return content
    return (
      <Popover visible placement='bottomLeft' overlayClassName='flat-cell-popover' title={column.label}
        content={(
          <CellEditor target={target} row={row} references={cell.references} saving={cell.saving}
            onCommit={(value) => cell.commit(row.id, column.edit, value)} onCancel={cell.cancel}/>
        )}>
        <div className='flat-cell--active'>{content}</div>
      </Popover>
    )
  }
  // a double click anywhere in the cell changes it (not the line's click, which opens the flat)
  const cellProps = (column) => (row) => (targetOf(column)
    ? {
      className: 'flat-cell--editable',
      onDoubleClick: (event) => {
        if (!event.currentTarget.contains(event.target)) return
        event.stopPropagation()
        cell.start(row.id, column.edit)
      },
    }
    : {})
  return [
    {
      key: 'actions',
      title: '',
      width: onStock ? 92 : 64,
      fixed: 'left',
      render: (row) => (
        <span className='flats-actions' onClick={(e) => e.stopPropagation()}>
          <Tooltip title={canEdit ? 'Изменить' : 'Открыть'}>
            <Button type='text' size='small' icon={canEdit ? <EditOutlined/> : <EyeOutlined/>} onClick={() => onEdit(row.id)}
              aria-label={`${canEdit ? 'Изменить' : 'Открыть'} квартиру ${row.id}`}/>
          </Tooltip>
          <Tooltip title='Открыть на портале'>
            <Button type='text' size='small' icon={<ExportOutlined/>} href={`${PORTAL}/apartments/${row.id}`} target='_blank'
              rel='noopener noreferrer' aria-label={`Квартира ${row.id} на портале`}/>
          </Tooltip>
          {onStock && (
            <Popconfirm title={row.stock ? 'Достать квартиру из Запаса?' : 'Отправить квартиру в Запас?'} okText={row.stock ? 'Из Запаса' : 'В Запас'}
              cancelText='Отмена' placement='right' onConfirm={() => onStock(row)}>
              <Tooltip title={row.stock ? 'Из Запаса' : 'В Запас'}>
                <Button type='text' size='small' className={row.stock ? 'flats-stock-button flats-stock-button--in' : 'flats-stock-button'}
                  icon={row.stock ? <ExportOutlined rotate={180}/> : <InboxOutlined/>} aria-label={`${row.stock ? 'Из Запаса' : 'В Запас'}: квартира ${row.id}`}/>
              </Tooltip>
            </Popconfirm>
          )}
        </span>
      ),
    },
    ...shown.filter((key) => byKey[key]).map((key) => {
      const column = byKey[key]
      const title = column.title && column.title.length > 14 ? <Tooltip title={column.title} mouseEnterDelay={0.4}><span>{column.title}</span></Tooltip> : column.title
      return {
        key,
        title,
        width: column.width,
        align: column.align,
        ellipsis: column.ellipsis,
        render: renderCell(column),
        onCell: cellProps(column),
        ...(column.sort ? {sorter: true, sortOrder: orderOf(sort, column.sort), sortDirections: ['ascend', 'descend']} : {}),
      }
    }),
  ]
}

export const ColumnsButton = ({onClick}) => <Button icon={<SettingOutlined/>} onClick={onClick}>Колонки</Button>

// «Колонки»: the shown ones in their order (up, down, away), and every field to tick, by group, with a search
export const ColumnsDrawer = ({open, onClose, catalogue, shown, onChange}) => {
  const [query, setQuery] = useState('')
  const byKey = Object.fromEntries(catalogue.flatMap((g) => g.columns).map((c) => [c.key, c]))
  const list = shown.filter((key) => byKey[key])
  const move = (i, step) => {
    const next = [...list]
    const [key] = next.splice(i, 1)
    next.splice(i + step, 0, key)
    onChange(next)
  }
  const q = query.trim().toLowerCase().replace(/ё/g, 'е')
  const matches = (c) => !q || String(c.label).toLowerCase().replace(/ё/g, 'е').includes(q) || c.key.toLowerCase().includes(q)
  return (
    <Drawer
      title='Колонки таблицы'
      placement='right'
      width={Math.min(780, window.innerWidth)}
      visible={open}
      onClose={onClose}
      footer={(
        <div className='apartment-filters__foot'>
          <Button onClick={() => onChange(STANDARD_COLUMNS)}>Как было по умолчанию</Button>
          <Button type='primary' onClick={onClose}>Готово</Button>
        </div>
      )}
    >
      <div className='flats-columns'>
        <section className='flats-columns__shown'>
          <h3>Показаны · {list.length}</h3>
          <p className='cell-hint'>Колонки сохраняются за вами и открываются так на любом компьютере.</p>
          {list.map((key, i) => (
            <div key={key} className='flats-columns__item'>
              <span className='flats-columns__name'>{byKey[key].label}</span>
              <Button type='text' size='small' icon={<ArrowUpOutlined/>} disabled={i === 0} onClick={() => move(i, -1)} aria-label='Выше'/>
              <Button type='text' size='small' icon={<ArrowDownOutlined/>} disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label='Ниже'/>
              <Button type='text' size='small' icon={<CloseOutlined/>} onClick={() => onChange(list.filter((k) => k !== key))} aria-label='Убрать'/>
            </div>
          ))}
        </section>
        <section className='flats-columns__all'>
          <Input.Search allowClear placeholder='Найти поле' value={query} onChange={(e) => setQuery(e.target.value)}/>
          {catalogue.map((group) => {
            const columns = group.columns.filter(matches)
            if (!columns.length) return null
            return (
              <div key={group.title} className='flats-columns__group'>
                <div className='flats-columns__head'>{group.title}</div>
                {columns.map((c) => (
                  <Checkbox key={c.key} checked={list.includes(c.key)}
                    onChange={(e) => onChange(e.target.checked ? [...list, c.key] : list.filter((k) => k !== c.key))}>
                    {c.label}
                  </Checkbox>
                ))}
              </div>
            )
          })}
        </section>
      </div>
    </Drawer>
  )
}
